import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import "./AddAssignment.css";

const AddAssignment = ({ assignment, closeForm, onAssignmentAdded }) => {
  const [className, setClassName] = useState("");
  const [classes, setClasses] = useState([]);
  const [section, setSection] = useState("");
  const [sections, setSections] = useState([]);
  const [subject, setSubject] = useState("");
  const [subjects, setSubjects] = useState([]);
  const [date, setDate] = useState("");
  const [day, setDay] = useState("");
  const [attachmentType, setAttachmentType] = useState("text");
  const [textMessage, setTextMessage] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [existingAttachmentUrl, setExistingAttachmentUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [error, setError] = useState(null);
  const [sectionError, setSectionError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      const teacherPhone = localStorage.getItem("userPhone");
      if (!teacherPhone) {
        toast.error("Mobile number not found in local storage.");
        setError("Mobile number not found.");
        setIsLoadingData(false);
        return;
      }

      try {
        setIsLoadingData(true);
        setError(null);
        setSectionError(null);

        const [classRes, sectionRes, subjectRes] = await Promise.all([
          axios.get(`https://aadyaacademy.org/api/get-teacherclass/${teacherPhone}`),
          axios.get(`https://aadyaacademy.org/api/get-section/${teacherPhone}`),
          axios.get(`https://aadyaacademy.org/api/api/subjects`),
        ]);

        // Convert string array to array of objects
        const classData = Array.isArray(classRes.data.class)
          ? classRes.data.class.map((cls, idx) => ({ id: idx + 1, name: cls }))
          : [];
        setClasses(classData);
        setClassName("");

        const sectionData = Array.isArray(sectionRes.data.section)
          ? sectionRes.data.section.map((sec, idx) => ({ id: idx + 1, name: sec }))
          : [];
        setSections(sectionData);
        setSection("");

        if (sectionData.length === 0) {
          setSectionError("No sections available for this teacher.");
          toast.warn("No sections available for this teacher.");
        }

        const subjectData = Array.isArray(subjectRes.data)
          ? subjectRes.data.map((sub) => ({ id: sub.id, name: sub.names }))
          : [];
        setSubjects(subjectData);
        setSubject("");

        if (subjectData.length === 0) {
          toast.warn("No subjects available.");
        }
      } catch (error) {
        setError("Failed to load data.");
        toast.error("Failed to load data.");
      } finally {
        setIsLoadingData(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const today = new Date();
    const formattedDate = today.toLocaleDateString("en-CA");
    const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
    setDate(formattedDate);
    setDay(days[today.getDay()]);
  }, []);

  useEffect(() => {
    if (assignment) {
      setClassName(assignment.class || "");
      setSection(assignment.section || "");
      setSubject(assignment.subject || "");
      setExistingAttachmentUrl(assignment.attachmentUrl || "");
      setTextMessage(assignment.text_message || "");
      setAttachmentType(assignment.attachmentUrl ? "file" : "text");
    }
  }, [assignment]);

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!className || !section || !subject) {
      toast.error("Please select class, section and subject.");
      return;
    }

    const formData = new FormData();
    formData.append("class", className);
    formData.append("section", section);
    formData.append("subject", subject);
    formData.append("date", date);
    formData.append("day", day);

    if (attachmentType === "text") {
      formData.append("text_message", textMessage);
    } else if (attachment) {
      formData.append("attachment", attachment);
    } else if (existingAttachmentUrl) {
      formData.append("attachmentUrl", existingAttachmentUrl);
    }

    try {
      setLoading(true);
      let response;

      if (assignment) {
        response = await axios.put(
          `https://aadyaacademy.org/api/assignments/${assignment.id}`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        toast.success("Assignment updated successfully!", { autoClose: 2000 });
      } else {
        response = await axios.post(
          "https://aadyaacademy.org/api/assignments",
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        toast.success("Assignment submitted successfully!", { autoClose: 2000 });
      }

      if (onAssignmentAdded) {
        onAssignmentAdded({
          id: response.data.id || assignment?.id,
          class: className,
          section: section,
          subject: subject,
          date: date,
          day: day,
          text_message: attachmentType === "text" ? textMessage : "",
          attachmentUrl: response.data.attachmentUrl || existingAttachmentUrl || "",
        });
      }

      closeForm();
    } catch (error) {
      toast.error("Failed to submit assignment.");
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAttachment(file);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`https://aadyaacademy.org/api/assignments/${assignment.id}`);
      toast.success("Assignment deleted successfully!");
      closeForm();
    } catch (err) {
      toast.error("Error deleting assignment.");
    }
  };

  if (isLoadingData) return <div>Loading data...</div>;
  if (error)
    return (
      <div>
        <p>{error}</p>
        <button onClick={closeForm}>Close</button>
      </div>
    );

  return (
    <div className="add-assignment-container">
      <div className="add-assignment-content">
        <h2>{assignment ? "Edit Assignment" : "Add New Assignment"}</h2>
        {message && <div className="message">{message}</div>}
        <form onSubmit={handleFormSubmit}>
          <div className="grade-subject">
            <div className="form-group-grade">
              <label>Grade:</label>
              <select value={className} onChange={(e) => setClassName(e.target.value)} required>
                <option value="">Select Grade</option>
                {classes.map((cls) => (
                  <option key={cls.id} value={cls.name}>
                    {cls.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group-section">
              <label>Section:</label>
              <select value={section} onChange={(e) => setSection(e.target.value)} required>
                <option value="">Select Section</option>
                {sections.map((sec) => (
                  <option key={sec.id} value={sec.name}>
                    {sec.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group-subject">
              <label>Subject:</label>
              <select value={subject} onChange={(e) => setSubject(e.target.value)} required>
                <option value="">Select Subject</option>
                {subjects.map((sub) => (
                  <option key={sub.id} value={sub.name}>
                    {sub.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="date-day">
            <div className="form-group-date">
              <label>Date:</label>
              <div className="date-value">{date}</div>
            </div>
            <div className="form-group-day">
              <label>Day:</label>
              <div className="day-value">{day}</div>
            </div>
          </div>
          <div className="form-group-attachment">
            <label>Attachment Type:</label>
            <select value={attachmentType} onChange={(e) => setAttachmentType(e.target.value)}>
              <option value="text">Text Message</option>
              <option value="file">File Upload</option>
            </select>
          </div>
          {attachmentType === "text" ? (
            <div className="form-group-text-message">
              <label>Text Message:</label>
              <textarea value={textMessage} onChange={(e) => setTextMessage(e.target.value)} />
            </div>
          ) : (
            <div className="form-group-file">
              <input type="file" accept=".pdf,.doc,.docx,.jpg,.png" onChange={handleFileChange} />
              {attachment && <p>Uploaded File: {attachment.name}</p>}
              {existingAttachmentUrl && !attachment && (
                <p>
                  Existing File:{" "}
                  <a href={existingAttachmentUrl} target="_blank" rel="noopener noreferrer">
                    View File
                  </a>
                </p>
              )}
            </div>
          )}
          <div className="assignment-buttons">
            <button type="submit" disabled={loading}>
              {loading ? "Submitting..." : assignment ? "Update" : "Submit"}
            </button>
            <button type="button" onClick={closeForm}>
              Cancel
            </button>
            {assignment && (
              <button type="button" onClick={handleDelete}>
                Delete
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddAssignment;
